import { useState, useEffect } from 'react'
import { FileText, Copy, PlusCircle, Loader } from 'lucide-react'
import { getAllVersions } from '../../services/questionnaireService'

/**
 * TemplateSelector Component
 * Allows user to choose how to start creating a questionnaire
 */
function TemplateSelector({ onSelect, onCancel }) {
  const [versions, setVersions] = useState([])
  const [loading, setLoading] = useState(true)
  const [selectedTemplate, setSelectedTemplate] = useState('active')
  const [selectedVersion, setSelectedVersion] = useState('')

  useEffect(() => {
    fetchVersions()
  }, [])

  const fetchVersions = async () => {
    try {
      const response = await getAllVersions(false) // Don't include deleted
      setVersions(response.data.versions)
      
      // Auto-select active version
      const activeVersion = response.data.versions.find(v => v.isActive)
      if (activeVersion) {
        setSelectedVersion(activeVersion.version)
      }
    } catch (error) {
      console.error('Error fetching versions:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleConfirm = () => {
    if (selectedTemplate === 'scratch') {
      onSelect(null) // Start from scratch
    } else if (selectedTemplate === 'active') {
      const activeVersion = versions.find(v => v.isActive)
      onSelect(activeVersion?.version)
    } else if (selectedTemplate === 'version' && selectedVersion) {
      onSelect(selectedVersion)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader className="w-8 h-8 animate-spin text-blue-600" />
        <span className="ml-3 text-gray-600">Chargement des versions...</span>
      </div>
    )
  }

  const activeVersion = versions.find(v => v.isActive)

  return (
    <div className="bg-white rounded-lg shadow-lg border border-gray-200 p-6">
      <h2 className="text-2xl font-bold text-gray-900 mb-2">
        Créer une nouvelle version
      </h2>
      <p className="text-gray-600 mb-6">
        Choisissez comment vous souhaitez commencer
      </p>

      <div className="space-y-4">
        {/* Option 1: From Active Version */}
        {activeVersion && (
          <label className="block cursor-pointer">
            <div
              className={`border-2 rounded-lg p-4 transition-all ${
                selectedTemplate === 'active'
                  ? 'border-blue-600 bg-blue-50'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <div className="flex items-start space-x-3">
                <input
                  type="radio"
                  name="template"
                  value="active"
                  checked={selectedTemplate === 'active'}
                  onChange={(e) => setSelectedTemplate(e.target.value)}
                  className="mt-1"
                />
                <div className="flex-1">
                  <div className="flex items-center space-x-2">
                    <Copy className="w-5 h-5 text-blue-600" />
                    <h3 className="font-semibold text-gray-900">
                      Dupliquer la version active
                    </h3>
                    <span className="px-2 py-1 text-xs bg-green-100 text-green-800 rounded">
                      Active
                    </span>
                  </div>
                  <p className="mt-1 text-sm text-gray-600">
                    Version {activeVersion.version} •{' '}
                    {activeVersion.summary.totalThemes} thèmes •{' '}
                    {activeVersion.summary.totalQuestions} questions
                  </p>
                  <p className="mt-1 text-xs text-gray-500">
                    Recommandé pour modifier le questionnaire existant
                  </p>
                </div>
              </div>
            </div>
          </label>
        )}

        {/* Option 2: From Specific Version */}
        <label className="block cursor-pointer">
          <div
            className={`border-2 rounded-lg p-4 transition-all ${
              selectedTemplate === 'version'
                ? 'border-blue-600 bg-blue-50'
                : 'border-gray-200 hover:border-gray-300'
            }`}
          >
            <div className="flex items-start space-x-3">
              <input
                type="radio"
                name="template"
                value="version"
                checked={selectedTemplate === 'version'}
                onChange={(e) => setSelectedTemplate(e.target.value)}
                className="mt-1"
              />
              <div className="flex-1">
                <div className="flex items-center space-x-2">
                  <FileText className="w-5 h-5 text-purple-600" />
                  <h3 className="font-semibold text-gray-900">
                    Partir d'une version spécifique
                  </h3>
                </div>
                <p className="mt-1 text-sm text-gray-600">
                  Choisissez une version dans l'historique
                </p>
                
                {selectedTemplate === 'version' && (
                  <select
                    value={selectedVersion}
                    onChange={(e) => setSelectedVersion(e.target.value)}
                    className="mt-3 w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">-- Sélectionner une version --</option>
                    {versions.map((v) => (
                      <option key={v.version} value={v.version}>
                        v{v.version} - {v.summary.totalThemes} thèmes,{' '}
                        {v.summary.totalQuestions} questions
                        {v.isActive ? ' (active)' : ''}
                      </option>
                    ))}
                  </select>
                )}
              </div>
            </div>
          </div>
        </label>

        {/* Option 3: From Scratch */}
        <label className="block cursor-pointer">
          <div
            className={`border-2 rounded-lg p-4 transition-all ${
              selectedTemplate === 'scratch'
                ? 'border-blue-600 bg-blue-50'
                : 'border-gray-200 hover:border-gray-300'
            }`}
          >
            <div className="flex items-start space-x-3">
              <input
                type="radio"
                name="template"
                value="scratch"
                checked={selectedTemplate === 'scratch'}
                onChange={(e) => setSelectedTemplate(e.target.value)}
                className="mt-1"
              />
              <div className="flex-1">
                <div className="flex items-center space-x-2">
                  <PlusCircle className="w-5 h-5 text-green-600" />
                  <h3 className="font-semibold text-gray-900">
                    Créer à partir de zéro
                  </h3>
                </div>
                <p className="mt-1 text-sm text-gray-600">
                  Commencer avec un questionnaire vide
                </p>
                <p className="mt-1 text-xs text-gray-500">
                  Pour créer un questionnaire complètement nouveau
                </p>
              </div>
            </div>
          </div>
        </label>
      </div>

      {/* Actions */}
      <div className="mt-6 flex items-center justify-end space-x-3">
        {onCancel && (
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Annuler
          </button>
        )}
        <button
          type="button"
          onClick={handleConfirm}
          disabled={selectedTemplate === 'version' && !selectedVersion}
          className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Continuer
        </button>
      </div>
    </div>
  )
}

export default TemplateSelector

